import { useEffect, useState } from 'react';
import { ArrowLeft, Bot, BotOff, Check, ChevronDown, Clock, Flag, MoreHorizontal, PauseCircle, PlayCircle, RotateCcw, Star, Tag, Ticket, UserCheck, Wand2, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useResource } from '../hooks/useResource';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { api, directoryApi, type DirectoryAgent } from '../lib/api';
import type { Conversation, Label, Priority } from '../lib/types';
import { ConsentBadge } from './ConsentBadge';
import { ContextMenu, type MenuNode } from './ContextMenu';
import { CreateTicketModal } from './CreateTicketModal';
import { identityTier, TIER_COLORS, TIER_LEGEND, type IdentityTier } from '../lib/identityTier';
import { sentimentMeta } from '../lib/sentiment';
import { useUiMode } from '../lib/uiMode';

const PRIORITY_OPTIONS: Priority[] = ['low', 'medium', 'high', 'urgent'];

const PRIORITY_BG: Record<Priority, string> = {
  urgent: 'bg-red-100 text-red-900 border-red-200',
  high: 'bg-amber-100 text-amber-900 border-amber-200',
  medium: 'bg-indigo-100 text-indigo-900 border-indigo-200',
  low: 'bg-gray-100 text-gray-700 border-gray-200',
};

interface Props {
  chat: Conversation;
  tenantCode: string;
  agentUid: string;
  onAction: () => void;
}

export function ChatHeader({ chat, tenantCode, agentUid, onAction }: Props) {
  // Simple view lays the header out in three zones — left: who this is and what
  // state it is in; centre: who's handling it; right: Resolve as the one primary
  // button, with star / waiting / snooze / priority / labels / ticket (and the
  // flag review, when there is one) folded into an overflow menu. Advanced keeps
  // every control inline exactly as it was, and adds the secondary meta
  // (consent, sentiment, CSAT, snooze-until).
  const { advanced } = useUiMode();
  const { items: labels } = useResource<Label>('labels');
  const [busy, setBusy] = useState(false);
  const [showLabelMenu, setShowLabelMenu] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [noticeMsg, setNoticeMsg] = useState<string | null>(null);
  const [overflowAt, setOverflowAt] = useState<{ x: number; y: number } | null>(null);
  const [showTicket, setShowTicket] = useState(false);
  const [dir, setDir] = useState<DirectoryAgent[]>([]);
  useEffect(() => {
    directoryApi.listAgentsDirectory(tenantCode).then((r) => setDir(r.items));
  }, [tenantCode]);
  const labelMenuRef = useOutsideClick<HTMLDivElement>(showLabelMenu, () => setShowLabelMenu(false));

  async function run<T>(fn: () => Promise<T>) {
    setBusy(true);
    setErrorMsg(null);
    setNoticeMsg(null);
    try {
      await fn();
      onAction();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  function snooze(hours: number) {
    return run(() => api.snooze(tenantCode, chat.id, agentUid, Date.now() + hours * 60 * 60 * 1000));
  }

  // Park / un-park ("waiting"): a snooze with no wake time, so the chat sits in
  // its own tab until someone brings it back rather than reappearing at 9am.
  function park(reason: 'customer' | 'internal') {
    return run(() => api.park(tenantCode, chat.id, reason));
  }

  function unpark() {
    return run(() => api.unpark(tenantCode, chat.id));
  }

  // `labels` is typed required but is genuinely absent on older chat docs — and
  // this block is no longer advanced-only, so a missing array would now blank
  // the header for every agent, not just the ones who opted into the density.
  const chatLabels = chat.labels ?? [];

  function toggleLabel(labelName: string) {
    const has = chatLabels.includes(labelName);
    return run(() =>
      api.setLabels(tenantCode, chat.id, agentUid, has ? undefined : [labelName], has ? [labelName] : undefined)
    );
  }

  // Simple view only. The secondary actions that used to sit inline competing
  // with Resolve for the agent's eye — same calls, one click further away.
  // Advanced never renders this: every one of them is still a button up top.
  function overflowItems(): MenuNode[] {
    const items: MenuNode[] = [
      {
        id: 'h-star',
        label: chat.starred ? 'Unstar conversation' : 'Star conversation',
        icon: <Star className={`w-3.5 h-3.5 ${chat.starred ? 'fill-amber-500 text-amber-500' : ''}`} />,
        onSelect: () => run(() => api.setStar(tenantCode, chat.id, agentUid, !chat.starred)),
      },
      {
        // Parking is NOT advanced-only tooling any more: the simple tab bar
        // spends one of its three slots on "Waiting", and the rail lists both
        // parked queues outright. With this only in the advanced WaitMenu, the
        // default view showed a queue an agent could neither fill nor empty.
        id: 'h-wait',
        label: 'Move to waiting',
        icon: <PauseCircle className="w-3.5 h-3.5" />,
        submenu: [
          {
            id: 'h-wt-customer',
            label: 'Waiting for customer',
            checked: chat.waitReason === 'customer',
            onSelect: () => park('customer'),
          },
          {
            id: 'h-wt-internal',
            label: 'Waiting on internal team',
            checked: chat.waitReason === 'internal',
            onSelect: () => park('internal'),
          },
          { divider: true },
          {
            id: 'h-wt-none',
            label: 'Back to the queue',
            icon: <PlayCircle className="w-3.5 h-3.5" />,
            disabled: !chat.waitReason,
            onSelect: () => unpark(),
          },
        ],
      },
      {
        id: 'h-snooze',
        label: 'Snooze',
        icon: <Clock className="w-3.5 h-3.5" />,
        submenu: snoozePresets().map((p) => ({
          id: `h-sn-${p.hours}`,
          label: p.label,
          onSelect: () => snooze(p.hours),
        })),
      },
      {
        id: 'h-priority',
        label: 'Priority',
        icon: <Flag className="w-3.5 h-3.5" />,
        submenu: PRIORITY_OPTIONS.map((p) => ({
          id: `h-pr-${p}`,
          label: p.charAt(0).toUpperCase() + p.slice(1),
          checked: chat.priority === p,
          onSelect: () => run(() => api.setPriority(tenantCode, chat.id, agentUid, p)),
        })),
      },
      {
        // Labels are NOT advanced-only anywhere — the chips stay on screen in
        // both views; this is where simple view does the adding and removing.
        id: 'h-labels',
        label: 'Labels',
        icon: <Tag className="w-3.5 h-3.5" />,
        submenu:
          labels.length === 0
            ? [{ id: 'h-lb-empty', label: 'No labels yet. Create in Settings.', disabled: true }]
            : labels.map((l) => ({
                id: `h-lb-${l.id}`,
                label: l.name,
                checked: chatLabels.includes(l.name),
                onSelect: () => toggleLabel(l.name),
              })),
      },
      { divider: true },
      {
        id: 'h-ticket',
        label: 'Create ticket…',
        icon: <Ticket className="w-3.5 h-3.5" />,
        disabled: !chat.contactId,
        onSelect: () => setShowTicket(true),
      },
    ];
    // First when it applies: it is the whole reason the agent is looking at this
    // chat. The AI Flagged queue is reachable in the default view too (the tab
    // bar's "More", and the rail lists it), so the one call that clears a flag
    // cannot live behind `advanced` — the queue would only ever grow.
    if (chat.aiFlagged) {
      items.unshift(
        {
          id: 'h-flag-reviewed',
          label:
            chat.aiFlagged.source === 'ai_error'
              ? 'AI errored · Mark reviewed'
              : 'Flagged · Mark reviewed',
          icon: <Flag className="w-3.5 h-3.5 text-rose-600" />,
          onSelect: () => run(() => api.flagReviewed(tenantCode, agentUid, chat.id)),
        },
        { divider: true },
      );
    }
    return items;
  }

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-2 flex flex-col gap-1.5 flex-shrink-0">
      {/* Simple view: three zones, separated by a wider gap than the controls
          inside them, so the eye reads left / centre / right and not ten peers
          in a row. Advanced: one row of controls, as before. */}
      <div
        className={`flex flex-wrap items-start justify-between ${
          advanced ? 'gap-2' : 'gap-x-5 gap-y-2'
        }`}
      >
        <Link
          to="/inbox"
          className="md:hidden flex-shrink-0 -ml-1.5 mr-0.5 mt-0.5 p-1.5 rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200"
          title="Back to conversations"
          aria-label="Back to conversations"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        {/* LEFT zone: who this is, and what state the conversation is in. */}
        <div className="min-w-0 flex-1">
          <div
            className={
              advanced
                ? 'flex flex-wrap items-start justify-between gap-x-3 gap-y-1'
                : 'flex flex-wrap items-center gap-x-2 gap-y-1'
            }
          >
          <div className="relative group text-sm font-semibold text-gray-900 flex items-center gap-1.5 min-w-0 max-w-full">
            <IdentityBadge chat={chat} />
            <span className="truncate cursor-default">
              {chat.contactName ||
                chat.contactEmail ||
                chat.contactPhone ||
                (chat.contactId.startsWith('anon-') ? 'Anonymous' : chat.contactId)}
            </span>
            <IdentityLegend current={identityTier(chat).tier} />
          </div>
          {/* Labels render in BOTH densities. The AI already tags every
              conversation; gating the chips + picker on `advanced` (which
              defaults to novice) is the whole reason tagging read as a missing
              feature to the people testing it. Simple view keeps the chips
              right here — they are state, they belong in the left zone — and
              moves only the EDITING (add/remove) into the overflow menu on the
              right, which is one click away in the same header. Nothing
              label-shaped is advanced-only. */}
          {(advanced || chatLabels.length > 0) && (
          <div
            className={`flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0 ${advanced ? 'justify-end' : ''}`}
          >
            {chatLabels.map((name) => {
              const def = labels.find((l) => l.name === name);
              const chipStyle = { background: def?.color || '#9ca3af' };
              return advanced ? (
                <button
                  key={name}
                  onClick={() => toggleLabel(name)}
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full text-white hover:opacity-80"
                  style={chipStyle}
                  title="Click to remove"
                >
                  {name} ×
                </button>
              ) : (
                <span
                  key={name}
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full text-white"
                  style={chipStyle}
                  title="Label — edit labels from the ⋯ menu"
                >
                  {name}
                </span>
              );
            })}
            {advanced && (
            <div className="relative" ref={labelMenuRef}>
              <button
                type="button"
                onClick={() => setShowLabelMenu((v) => !v)}
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-50 text-gray-600 border border-dashed border-gray-400 hover:bg-gray-100"
              >
                <Plus className="w-3 h-3" /> label
              </button>
              {showLabelMenu && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg min-w-[180px] z-10 py-1">
                  {labels.length === 0 && (
                    <div className="px-3 py-2 text-xs text-gray-500">No labels yet. Create in Settings.</div>
                  )}
                  {labels.map((l) => {
                    const has = chatLabels.includes(l.name);
                    return (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => {
                          toggleLabel(l.name);
                          setShowLabelMenu(false);
                        }}
                        className="flex items-center w-full px-3 py-1.5 text-sm hover:bg-gray-50 text-left"
                      >
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                          style={{ background: l.color }}
                        />
                        <span className="flex-1">{l.name}</span>
                        {has && <Check className="w-3.5 h-3.5 text-primary-600" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            )}
          </div>
          )}
          </div>
          <div className="text-xs text-gray-500 flex flex-wrap gap-x-1.5 gap-y-1 items-center mt-0.5">
            <span className="font-medium uppercase tracking-wide">{chat.channel}</span>
            {advanced && <ConsentBadge contactId={chat.contactId} channel={chat.channel} />}
            <span className="text-gray-300">·</span>
            <span>{chat.status}</span>
            {(chat.contactEmail || chat.contactPhone) && (
              <>
                <span className="text-gray-300">·</span>
                <span className="truncate max-w-[14rem]">{chat.contactEmail || chat.contactPhone}</span>
              </>
            )}
            {chat.aiHandled === false && chat.status !== 'resolved' && chat.status !== 'snoozed' && (
              <span
                className="inline-flex items-center gap-1 font-semibold text-rose-700 bg-rose-100 border border-rose-300 rounded-full px-2 py-0.5 animate-pulse"
                title="The AI handed this conversation to a human — a reply is needed now"
              >
                ⚠ Needs human reply
              </span>
            )}
            {advanced && <SentimentArc chat={chat} />}
            {advanced && typeof chat.csatScore === 'number' && (
              <>
                <span className="text-gray-300">·</span>
                <span
                  className="inline-flex items-center gap-0.5 font-medium text-amber-600"
                  title={chat.csatComment ? `CSAT comment: “${chat.csatComment}”` : 'Customer satisfaction rating'}
                >
                  {chat.csatScore}★ CSAT
                </span>
              </>
            )}
            {/* Parked chats snooze to a far-future date to stay out of the
                queues — printing that date would just read as broken, so say
                what it's actually waiting on instead. */}
            {chat.status === 'snoozed' && chat.waitReason ? (
              <>
                <span className="text-gray-300">·</span>
                <span className="font-medium text-amber-700">
                  waiting {chat.waitReason === 'customer' ? 'for customer' : 'on internal team'}
                </span>
              </>
            ) : (
              advanced && chat.snoozedUntil && chat.status === 'snoozed' && (
                <>
                  <span className="text-gray-300">·</span>
                  <span>until {new Date(chat.snoozedUntil).toLocaleString()}</span>
                </>
              )
            )}
            {chat.assigneeUid && (
              <>
                <span className="text-gray-300">·</span>
                <span
                  className="truncate max-w-[12rem]"
                  title={`Assigned to ${chat.assigneeUid === agentUid ? 'you' : chat.assigneeName ?? dir.find((d) => d.uid === chat.assigneeUid)?.displayName ?? chat.assigneeUid}`}
                >
                  assigned to{' '}
                  {chat.assigneeUid === agentUid
                    ? 'you'
                    : chat.assigneeName ?? dir.find((d) => d.uid === chat.assigneeUid)?.displayName ?? `${chat.assigneeUid.slice(0, 6)}… (agent)`}
                </span>
              </>
            )}
          </div>
        </div>
        {/* CENTRE zone (simple view): who is handling this conversation. The
            control itself is the same component the advanced cluster renders —
            it just gets its own slot instead of queueing behind six buttons. */}
        {!advanced && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <AssignMenu
              chat={chat}
              tenantCode={tenantCode}
              agentUid={agentUid}
              dir={dir}
              disabled={busy}
              run={run}
            />
          </div>
        )}
        {/* RIGHT zone: in simple view one primary button + the overflow; in
            advanced the full inline cluster, unchanged. */}
        <div className="flex gap-1.5 flex-wrap justify-end min-w-0">
          {advanced && (
            <select
              value={chat.priority}
              onChange={(e) => run(() => api.setPriority(tenantCode, chat.id, agentUid, e.target.value as Priority))}
              disabled={busy}
              className={`text-xs font-medium px-2 py-1.5 rounded border ${PRIORITY_BG[chat.priority]} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          )}
          {advanced && (
            <button
              onClick={() => run(() => api.setStar(tenantCode, chat.id, agentUid, !chat.starred))}
              disabled={busy}
              className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded border disabled:opacity-50 ${
                chat.starred
                  ? 'bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title={chat.starred ? 'Unstar conversation' : 'Star conversation (find it later via the Starred filter)'}
            >
              <Star className={`w-3.5 h-3.5 ${chat.starred ? 'fill-amber-500 text-amber-500' : ''}`} />
              {chat.starred ? 'Starred' : 'Star'}
            </button>
          )}
          {advanced && chat.aiFlagged && (
            <button
              onClick={() => run(() => api.flagReviewed(tenantCode, agentUid, chat.id))}
              disabled={busy}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded border bg-rose-50 border-rose-300 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
              title={
                chat.aiFlagged.source === 'ai_error'
                  ? 'The AI errored on this chat and it was flagged for review. Mark reviewed to clear it from the AI Flagged tab.'
                  : 'An agent flagged the AI answer here. Mark reviewed to clear it from the AI Flagged tab.'
              }
            >
              {chat.aiFlagged.source === 'ai_error' ? 'AI errored' : 'Flagged'} · Mark reviewed
            </button>
          )}
          {advanced && (
            <AssignMenu
              chat={chat}
              tenantCode={tenantCode}
              agentUid={agentUid}
              dir={dir}
              disabled={busy}
              run={run}
            />
          )}
          {advanced && (
            <WaitMenu disabled={busy} reason={chat.waitReason ?? null} onPark={park} onUnpark={unpark} />
          )}
          {advanced && <SnoozeMenu disabled={busy} onPick={snooze} />}
          {chat.status !== 'resolved' ? (
            <button
              onClick={() => run(() => api.setStatus(tenantCode, chat.id, agentUid, 'resolved'))}
              disabled={busy}
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" /> Resolve
            </button>
          ) : (
            <button
              onClick={() => run(() => api.setStatus(tenantCode, chat.id, agentUid, 'open'))}
              disabled={busy}
              className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reopen
            </button>
          )}
          {!advanced && (
            <button
              type="button"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setOverflowAt({ x: r.right, y: r.bottom + 4 });
              }}
              disabled={busy}
              className="inline-flex items-center px-2 py-1.5 rounded border bg-white border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="More — star, waiting, snooze, priority, labels, ticket"
              aria-label="More actions"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {errorMsg && (
        <div className="bg-red-50 text-red-800 px-3 py-2 rounded text-xs flex items-center justify-between">
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg(null)} className="ml-2 text-red-700 hover:text-red-900">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {noticeMsg && (
        <div className="bg-gray-50 border border-gray-200 text-gray-700 px-3 py-2 rounded text-xs flex items-center justify-between">
          <span>{noticeMsg}</span>
          <button onClick={() => setNoticeMsg(null)} className="ml-2 text-gray-500 hover:text-gray-700">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {overflowAt && (
        <ContextMenu
          x={overflowAt.x}
          y={overflowAt.y}
          items={overflowItems()}
          onClose={() => setOverflowAt(null)}
        />
      )}
      {/* Owned here rather than deep-linked into the customer panel (?cust=…):
          the panel only reads that param once, so a second Create ticket from
          the header would open nothing. */}
      {showTicket && chat.contactId && (
        <CreateTicketModal
          tenantCode={tenantCode}
          agentUid={agentUid}
          contactId={chat.contactId}
          chatId={chat.id}
          onClose={(msg) => {
            setShowTicket(false);
            if (msg) setNoticeMsg(msg);
          }}
        />
      )}
    </header>
  );
}

// Who is handling this conversation: AI take/return + assignment in one menu
// (agent feedback — these were three separate controls). Simple view gives it
// the header's centre zone, advanced keeps it inline in the action cluster;
// the control itself is identical in both.
function AssignMenu({
  chat,
  tenantCode,
  agentUid,
  dir,
  disabled,
  run,
}: {
  chat: Conversation;
  tenantCode: string;
  agentUid: string;
  dir: DirectoryAgent[];
  disabled: boolean;
  run: (fn: () => Promise<unknown>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClick<HTMLDivElement>(open, () => setOpen(false));
  const aiOn = chat.aiHandled !== false;
  const assigneeName = chat.assigneeUid
    ? dir.find((d) => d.uid === chat.assigneeUid)?.displayName ?? 'assigned'
    : null;
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded border disabled:opacity-50 ${
          aiOn
            ? 'bg-violet-50 border-violet-300 text-violet-700 hover:bg-violet-100'
            : assigneeName
              ? 'bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
        title="Who is handling this conversation"
      >
        {aiOn ? <Bot className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
        {aiOn ? 'AI handling' : assigneeName ? assigneeName : 'Unassigned'}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-30 min-w-[230px] max-h-80 overflow-y-auto py-1 text-xs">
          {aiOn ? (
            <button
              className="w-full text-left px-3 py-1.5 hover:bg-violet-50 flex items-center gap-2 text-violet-700"
              title="Disable AI auto-replies — you take over"
              onClick={() => {
                setOpen(false);
                run(() => api.toggleAi(tenantCode, agentUid, chat.id, true));
              }}
            >
              <BotOff className="w-3.5 h-3.5" /> Take over from AI
            </button>
          ) : (
            <button
              className="w-full text-left px-3 py-1.5 hover:bg-violet-50 flex items-center gap-2 text-violet-700"
              title="Re-enable AI auto-replies"
              onClick={() => {
                setOpen(false);
                run(() => api.toggleAi(tenantCode, agentUid, chat.id, false));
              }}
            >
              <Bot className="w-3.5 h-3.5" /> Return to AI
            </button>
          )}
          <div className="border-t border-gray-100 my-1" />
          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Assign to
          </div>
          {chat.assigneeUid !== agentUid && (
            <button
              className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2"
              title="Assign to yourself and take over from AI"
              onClick={() => {
                setOpen(false);
                run(async () => {
                  await api.assign(tenantCode, chat.id, agentUid, agentUid);
                  if (aiOn) await api.toggleAi(tenantCode, agentUid, chat.id, true);
                });
              }}
            >
              <UserCheck className="w-3.5 h-3.5" /> Me (take over)
            </button>
          )}
          <button
            className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2"
            title="Round-robin / least-loaded to the next available agent (inbox strategy)"
            onClick={() => {
              setOpen(false);
              run(() => api.autoAssign(tenantCode, chat.id, agentUid, chat.inboxId));
            }}
          >
            <Wand2 className="w-3.5 h-3.5" /> Auto — next available
          </button>
          <div className="border-t border-gray-100 my-1" />
          {dir.length === 0 && <div className="px-3 py-1.5 text-gray-400">No agents</div>}
          {dir.map((a) => (
            <button
              key={a.uid}
              className="w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center justify-between gap-2"
              onClick={() => {
                setOpen(false);
                run(() => api.assign(tenantCode, chat.id, agentUid, a.uid));
              }}
            >
              <span className="truncate">{a.displayName}</span>
              {chat.assigneeUid === a.uid && <Check className="w-3 h-3 text-green-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Customer-sentiment arc: Beginning → Now faces, from the live chat doc. Updated
// every inbound turn by the AI runner (including while a human agent handles the
// chat, since the brain still classifies each turn). Re-renders on every header
// onSnapshot update — no polling. Hidden until the first turn is classified.
function SentimentArc({ chat }: { chat: Conversation }) {
  const now = sentimentMeta(chat.sentimentLast);
  if (!now) return null;
  const first = sentimentMeta(chat.sentimentFirst) ?? now;
  const moved = chat.sentimentFirst && chat.sentimentLast && chat.sentimentFirst !== chat.sentimentLast;
  return (
    <>
      <span className="text-gray-300">·</span>
      <span
        className="inline-flex items-center gap-1"
        title={`Customer sentiment — Beginning: ${first.label} · Now: ${now.label}`}
        aria-label={`Customer sentiment, beginning ${first.label}, now ${now.label}`}
      >
        <first.Icon className="w-4 h-4" style={{ color: first.color }} aria-hidden />
        {moved && <span className="text-gray-300 text-[10px]">→</span>}
        {moved && <now.Icon className="w-4 h-4" style={{ color: now.color }} aria-hidden />}
        <span className="font-medium" style={{ color: now.color }}>{now.label}</span>
      </span>
    </>
  );
}

// Identity "traffic light" dot derived from the live chat doc (authLevel + channel).
// Re-renders with the header on every onSnapshot update — no polling.
function IdentityBadge({ chat }: { chat: Conversation }) {
  const { tier, label } = identityTier(chat);
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-black/5"
      style={{ background: TIER_COLORS[tier] }}
      title={label}
      aria-label={`Identity: ${label}`}
      role="img"
    />
  );
}

// Hover legend: explains all four traffic-light states, current one highlighted.
function IdentityLegend({ current }: { current: IdentityTier }) {
  return (
    <div className="hidden group-hover:block absolute top-full left-0 mt-1 z-40 w-72 rounded-lg border border-gray-200 bg-white shadow-lg p-2.5 text-left font-normal">
      <div className="text-[11px] font-semibold text-gray-700 mb-1.5">Identity &amp; verification</div>
      <ul className="space-y-1.5">
        {TIER_LEGEND.map((t) => (
          <li key={t.tier} className={`flex items-start gap-2 ${t.tier === current ? '' : 'opacity-55'}`}>
            <span
              className="mt-0.5 inline-block w-2.5 h-2.5 rounded-full ring-1 ring-black/5 flex-shrink-0"
              style={{ background: TIER_COLORS[t.tier] }}
            />
            <span className="text-[11px] leading-snug">
              <span className="font-medium text-gray-900">
                {t.title}
                {t.tier === current ? ' — current' : ''}
              </span>
              <span className="text-gray-500"> · {t.desc}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// "Move to waiting": park the conversation on the customer or on another team.
// Sibling of SnoozeMenu — snooze is "remind me in N hours", this is "there's
// nothing for us to do until they come back", so it has no timer and the chat
// lives in its own tab until an agent brings it back to the queue.
function WaitMenu({
  disabled,
  reason,
  onPark,
  onUnpark,
}: {
  disabled: boolean;
  reason: 'customer' | 'internal' | null;
  onPark: (reason: 'customer' | 'internal') => void;
  onUnpark: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClick<HTMLDivElement>(open, () => setOpen(false));
  const options: Array<{ label: string; value: 'customer' | 'internal' }> = [
    { label: 'Waiting for customer', value: 'customer' },
    { label: 'Waiting on internal team', value: 'internal' },
  ];
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded border disabled:opacity-50 ${
          reason
            ? 'bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
        title={
          reason
            ? 'Parked — bring it back to the queue when the answer arrives'
            : 'Park this conversation while you wait on the customer or another team'
        }
      >
        <PauseCircle className="w-3.5 h-3.5" />
        {reason ? (reason === 'customer' ? 'Waiting: customer' : 'Waiting: internal') : 'Waiting'}
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 py-1 min-w-[200px]">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => {
                onPark(o.value);
                setOpen(false);
              }}
              className="flex items-center w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              <span className="flex-1">{o.label}</span>
              {reason === o.value && <Check className="w-3.5 h-3.5 text-primary-600" />}
            </button>
          ))}
          {reason && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                type="button"
                onClick={() => {
                  onUnpark();
                  setOpen(false);
                }}
                className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                <PlayCircle className="w-3.5 h-3.5" /> Back to the queue
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Shared by the advanced Snooze button and the simple-view overflow menu, so
// flipping density never changes what you can snooze to. A function, not a
// const: "Tomorrow 9am" must not be frozen at module load.
function snoozePresets(): Array<{ label: string; hours: number }> {
  return [
    { label: '1 hour', hours: 1 },
    { label: '4 hours', hours: 4 },
    { label: 'Tomorrow 9am', hours: hoursUntil(9) },
    { label: 'Next week', hours: 24 * 7 },
  ];
}

function SnoozeMenu({ disabled, onPick }: { disabled: boolean; onPick: (hours: number) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClick<HTMLDivElement>(open, () => setOpen(false));
  const presets = snoozePresets();
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50"
      >
        <Clock className="w-3.5 h-3.5" /> Snooze
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 py-1 min-w-[140px]">
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                onPick(p.hours);
                setOpen(false);
              }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function hoursUntil(targetHour: number): number {
  const now = new Date();
  const target = new Date(now);
  target.setDate(now.getDate() + 1);
  target.setHours(targetHour, 0, 0, 0);
  return Math.max(1, Math.ceil((target.getTime() - now.getTime()) / (60 * 60 * 1000)));
}
